import React, { useContext, useState, useMemo, useRef } from "react";
import { ProdutosContext } from "../../../shared/contexts/ProdutosContext";
import { CategoriasContext } from "../../../shared/contexts/CategoriasContext";
import { fetchWithAuth, getToken } from "../../../shared/utils/auth";
import styles from "./ProdutoTabela.module.css";
import EditarProdutoPopUp from "./EditarProdutoPopUpAdmin";
import CadastrarProdutoPopUp from "../../../shared/components/CadastrarProdutoPopUp";
import { MdRestaurantMenu, MdAdd, MdLocalOffer, MdStar, MdUploadFile, MdSearch } from "react-icons/md";

const API_URL = process.env.REACT_APP_API_URL;

function PrecoFormatado({ valor, className, centavosClassName }) {
  const [inteiro, centavos] = Number(valor).toFixed(2).split(".");
  return (
    <span className={className}>
      R$ {inteiro}
      <span className={centavosClassName}>,{centavos}</span>
    </span>
  );
}

function ProdutoTabela({ produtosFiltradosExternos = null }) {
  const { produtos, setProdutos, carregarProdutos } = useContext(ProdutosContext);
  const { categorias } = useContext(CategoriasContext);

  const [precoFiltro, setPrecoFiltro] = useState("todos");
  const [busca, setBusca] = useState("");
  const [produtoEditando, setProdutoEditando] = useState(null);
  const [mostrarCadastro, setMostrarCadastro] = useState(false);
  const [produtoLancarQuantidade, setProdutoLancarQuantidade] = useState(null);
  const [novaQuantidade, setNovaQuantidade] = useState(0);
  const [produtoLancarPromocao, setProdutoLancarPromocao] = useState(null);
  const [novoDesconto, setNovoDesconto] = useState(0);
  const [mobileTab, setMobileTab] = useState("cardapio");
  const [mobilePromoItemId, setMobilePromoItemId] = useState("");
  const [mobilePromoDesconto, setMobilePromoDesconto] = useState("");

  // Importar cardápio via PDF — mesma lógica que já existia só na tela de
  // cadastro (CadastroProdutos.jsx), mas só alcançável ali pelo botão do
  // estado vazio (sem produto nenhum ainda). Uma vez que a loja já tem itens,
  // não tinha mais como importar um PDF nem no desktop nem no mobile — por
  // isso o botão fica aqui, dentro do próprio ProdutoTabela, que é usado nas
  // duas telas.
  const [mostrarImportarPdf, setMostrarImportarPdf] = useState(false);
  const [arquivoPdf, setArquivoPdf] = useState(null);
  const [carregandoPdf, setCarregandoPdf] = useState(false);
  const [resultadoPdf, setResultadoPdf] = useState("");
  const fileInputPdfRef = useRef(null);

  // Se a página passou produtos prontos, usa eles.
  const listaBase = produtosFiltradosExternos || produtos || [];

  // Mapa de id_categoria -> nome
  const mapaCategorias = useMemo(() => {
    const map = {};
    categorias.forEach((cat) => {
      map[cat.id_categoria] = cat.nome_categoria;
    });
    return map;
  }, [categorias]);

  // Filtra produtos
  const produtosFiltrados = useMemo(() => {
    let filtrados = [...listaBase];

    if (busca.trim() !== "") {
      filtrados = filtrados.filter((p) =>
        p.nome_item.toLowerCase().includes(busca.toLowerCase())
      );
    }

    if (precoFiltro !== "todos") {
      if (precoFiltro === "baixo") filtrados = filtrados.filter((p) => p.preco < 20);
      if (precoFiltro === "medio")
        filtrados = filtrados.filter((p) => p.preco >= 20 && p.preco <= 50);
      if (precoFiltro === "alto") filtrados = filtrados.filter((p) => p.preco > 50);
    }

    return filtrados;
  }, [listaBase, precoFiltro, busca]);

  // Agrupa os produtos filtrados por seção (categoria), na ordem em que as seções foram criadas.
  const secoes = useMemo(() => {
    const idsConhecidos = new Set(categorias.map((cat) => String(cat.id_categoria)));
    const porCategoria = new Map();
    const semSecao = [];

    produtosFiltrados.forEach((produto) => {
      const chave = produto.id_categoria != null ? String(produto.id_categoria) : null;
      if (chave && idsConhecidos.has(chave)) {
        if (!porCategoria.has(chave)) porCategoria.set(chave, []);
        porCategoria.get(chave).push(produto);
      } else {
        semSecao.push(produto);
      }
    });

    const grupos = categorias
      .filter((cat) => porCategoria.has(String(cat.id_categoria)))
      .map((cat) => ({
        id: String(cat.id_categoria),
        nome: cat.nome_categoria,
        produtos: porCategoria.get(String(cat.id_categoria)),
      }));

    if (semSecao.length > 0) {
      grupos.push({ id: "__sem_secao__", nome: "Sem seção", produtos: semSecao });
    }

    return grupos;
  }, [produtosFiltrados, categorias]);

  const produtosComPromocao = useMemo(
    () => listaBase.filter((p) => p.promocao === "sim"),
    [listaBase]
  );

  const handleArquivoPdf = (e) => {
    const arquivo = e.target.files[0];
    if (arquivo && arquivo.type === "application/pdf") {
      setArquivoPdf(arquivo);
      setResultadoPdf("");
    } else {
      alert("Por favor, selecione um arquivo PDF válido!");
    }
  };

  const fecharModalPdf = () => {
    setMostrarImportarPdf(false);
    setArquivoPdf(null);
    setResultadoPdf("");
    if (fileInputPdfRef.current) fileInputPdfRef.current.value = "";
  };

  const processarPdfCardapio = async () => {
    if (!arquivoPdf) {
      alert("Por favor, selecione um arquivo PDF primeiro!");
      return;
    }

    const estabelecimento_id = localStorage.getItem("estabelecimento_id");
    if (!estabelecimento_id) {
      alert("Erro: Estabelecimento não identificado. Por favor, faça login novamente.");
      return;
    }

    try {
      setCarregandoPdf(true);
      setResultadoPdf("");

      const formData = new FormData();
      formData.append("pdf", arquivoPdf);

      const token = getToken();
      const response = await fetch(`${API_URL}/extract-cardapio`, {
        method: "POST",
        // Não definir Content-Type manualmente: o navegador precisa gerar o
        // boundary do multipart/form-data sozinho.
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: "Erro desconhecido" }));
        throw new Error(errorData.error || errorData.details || "Erro ao processar PDF");
      }

      const data = await response.json();
      const produtosExtraidos = data.produtos;

      if (!produtosExtraidos || produtosExtraidos.length === 0) {
        setResultadoPdf("Nenhum produto foi encontrado no PDF.");
        return;
      }

      let sucessos = 0;
      let erros = 0;

      for (const produto of produtosExtraidos) {
        try {
          const responseCadastro = await fetchWithAuth(`${API_URL}/cadastroprodutos`, {
            method: "POST",
            body: JSON.stringify({
              nome_item: produto.nome || produto.name,
              descricao: produto.descricao || produto.description || "",
              preco: produto.preco || produto.price || 0,
              id_categoria: produto.id_categoria || null,
              imagem: "",
              disponivel: true,
              // Cardápio importado não traz contagem de estoque — deixa
              // ilimitado (null) em vez de 0 (que apareceria como "em falta").
              quantidade: null,
              id_estabelecimento: estabelecimento_id
            })
          });

          if (responseCadastro && responseCadastro.ok) {
            sucessos++;
          } else {
            erros++;
          }
        } catch (error) {
          console.error("Erro ao cadastrar produto:", error);
          erros++;
        }
      }

      setResultadoPdf(`Processamento concluído!\nProdutos cadastrados: ${sucessos}\nErros: ${erros}`);

      if (carregarProdutos) {
        await carregarProdutos();
      }

      setArquivoPdf(null);
      if (fileInputPdfRef.current) fileInputPdfRef.current.value = "";
    } catch (error) {
      console.error("Erro ao processar PDF:", error);
      setResultadoPdf(`Erro ao processar o arquivo PDF: ${error.message || "tente novamente."}`);
    } finally {
      setCarregandoPdf(false);
    }
  };

  const handleLancarQuantidade = async () => {
    if (!produtoLancarQuantidade || novaQuantidade < 0) {
      alert("Informe uma quantidade válida.");
      return;
    }

    // Log para debug
    console.log('Produto a atualizar:', produtoLancarQuantidade);
    console.log('Nova quantidade:', novaQuantidade);

    try {
      const API_URL = process.env.REACT_APP_API_URL;

      const payload = {
        id_item: produtoLancarQuantidade.id_item,
        nome_item: produtoLancarQuantidade.nome_item,
        descricao: produtoLancarQuantidade.descricao || '',
        preco: Number(produtoLancarQuantidade.preco),
        id_categoria: Number(produtoLancarQuantidade.id_categoria),
        disponivel: produtoLancarQuantidade.disponivel,
        quantidade: Number(novaQuantidade),
      };

      console.log('Payload enviado:', payload);

      const res = await fetchWithAuth(`${API_URL}/lancar-quantidade-item`, {
        method: "POST",
        body: JSON.stringify(payload),
      });

      if (!res) {
        // Token expirado ou inválido - fetchWithAuth já fez logout
        return;
      }

      const data = await res.json();
      if (data.success) {
        const novosProdutos = produtos.map((p) =>
          p.id_item === produtoLancarQuantidade.id_item
            ? { ...p, quantidade: novaQuantidade }
            : p
        );
        setProdutos(novosProdutos);
        setProdutoLancarQuantidade(null);
        setNovaQuantidade(0);
      } else {
        alert("Erro ao atualizar quantidade: " + data.message);
      }
    } catch (error) {
      console.error(error);
      alert("Erro ao atualizar quantidade.");
    }
  };

  const aplicarPromocao = async (idItem, desconto, ativar) => {
    try {
      const API_URL = process.env.REACT_APP_API_URL;

      const payload = {
        id_item: idItem,
        promocao: ativar ? 'sim' : 'nao',
        desconto: ativar ? Number(desconto) || 0 : null,
      };

      const res = await fetchWithAuth(`${API_URL}/lancar-promocao-item`, {
        method: "POST",
        body: JSON.stringify(payload),
      });

      if (!res) return false;

      const data = await res.json();
      if (data.success) {
        const novosProdutos = produtos.map((p) =>
          p.id_item === idItem
            ? { ...p, promocao: ativar ? 'sim' : 'nao', desconto: ativar ? Number(desconto) : null }
            : p
        );
        setProdutos(novosProdutos);
        return true;
      }

      alert("Erro ao atualizar promoção: " + data.message);
      return false;
    } catch (error) {
      console.error(error);
      alert("Erro ao atualizar promoção.");
      return false;
    }
  };

  const handleLancarPromocao = async (ativar) => {
    if (!produtoLancarPromocao) return;

    const sucesso = await aplicarPromocao(produtoLancarPromocao.id_item, novoDesconto, ativar);
    if (sucesso) {
      setProdutoLancarPromocao(null);
      setNovoDesconto(0);
    }
  };

  const handleLancarPromocaoMobile = async () => {
    if (!mobilePromoItemId || !mobilePromoDesconto || Number(mobilePromoDesconto) <= 0) {
      alert("Selecione um item e informe um desconto válido.");
      return;
    }

    const sucesso = await aplicarPromocao(Number(mobilePromoItemId), mobilePromoDesconto, true);
    if (sucesso) {
      setMobilePromoItemId("");
      setMobilePromoDesconto("");
    }
  };

  const handleRemoverPromocaoMobile = (produto) => {
    aplicarPromocao(produto.id_item, 0, false);
  };

  const handleToggleDisponibilidade = async (produto) => {
    const novaDisponibilidade = produto.disponivel?.toLowerCase() === "sim" ? "nao" : "sim";

    try {
      const API_URL = process.env.REACT_APP_API_URL;
      const res = await fetchWithAuth(`${API_URL}/atualizar-item-produto`, {
        method: "POST",
        body: JSON.stringify({
          id_item: produto.id_item,
          nome_item: produto.nome_item,
          descricao: produto.descricao || '',
          preco: produto.preco,
          id_categoria: produto.id_categoria,
          disponivel: novaDisponibilidade,
          // preserva null (estoque ilimitado) — não força pra 0
          quantidade: produto.quantidade,
        }),
      });

      if (!res) {
        // Token expirado ou inválido - fetchWithAuth já fez logout
        return;
      }

      const data = await res.json();
      if (data.success) {
        const novosProdutos = produtos.map((p) =>
          p.id_item === produto.id_item
            ? { ...p, disponivel: novaDisponibilidade }
            : p
        );
        setProdutos(novosProdutos);
      } else {
        alert("Erro ao atualizar disponibilidade: " + data.message);
      }
    } catch (error) {
      console.error(error);
      alert("Erro ao atualizar disponibilidade.");
    }
  };

  if (!listaBase || listaBase.length === 0) {
    return <p className={styles.msg}>Nenhum produto cadastrado.</p>;
  }

  return (
    <div className={styles.tabelaContainer}>
      {/* Abas Mobile */}
      <div className={styles.mobileTabs}>
        <button
          className={mobileTab === "cardapio" ? styles.mobileTabActive : styles.mobileTab}
          onClick={() => setMobileTab("cardapio")}
        >
          <MdRestaurantMenu /> Cardápio
        </button>
        <button
          className={mobileTab === "novoItem" ? styles.mobileTabActive : styles.mobileTab}
          onClick={() => setMobileTab("novoItem")}
        >
          <MdAdd /> Novo Item
        </button>
        <button
          className={mobileTab === "promocoes" ? styles.mobileTabActive : styles.mobileTab}
          onClick={() => setMobileTab("promocoes")}
        >
          <MdLocalOffer /> Promoções
        </button>
      </div>

      {/* Busca - Mobile */}
      {mobileTab === "cardapio" && (
        <div className={styles.novaSecaoMobileWrapper}>
          <div className={styles.buscaMobileWrapper}>
            <MdSearch className={styles.buscaMobileIcon} />
            <input
              type="text"
              placeholder="Pesquisar por nome..."
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              className={styles.campoBuscaMobile}
            />
          </div>
        </div>
      )}

      {/* Filtros Desktop */}
      <div className={styles.filtros}>
        <div className={styles.linhaBusca}>
          <div className={styles.inputWrapper}>
            <svg
              className={styles.searchIcon}
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-4.35-4.35M10.5 18a7.5 7.5 0 100-15 7.5 7.5 0 000 15z"
              />
            </svg>
            <input
              type="text"
              placeholder="Pesquisar por nome..."
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              className={styles.campoBusca}
            />
          </div>

          {/* Filtros de preço - Desktop */}
          <div className={styles.filtroGrupoDesktop}>
            <button
              className={precoFiltro === "todos" ? styles.ativo : ""}
              onClick={() => setPrecoFiltro("todos")}
            >
              Todos
            </button>
            <button
              className={precoFiltro === "baixo" ? styles.ativo : ""}
              onClick={() => setPrecoFiltro("baixo")}
            >
              &lt; R$20
            </button>
            <button
              className={precoFiltro === "medio" ? styles.ativo : ""}
              onClick={() => setPrecoFiltro("medio")}
            >
              R$20–R$50
            </button>
            <button
              className={precoFiltro === "alto" ? styles.ativo : ""}
              onClick={() => setPrecoFiltro("alto")}
            >
              &gt; R$50
            </button>
          </div>

          <button
            className={styles.btnImportarPdf}
            onClick={() => setMostrarImportarPdf(true)}
          >
            <MdUploadFile /> Importar PDF
          </button>

          <button
            className={styles.btnCadastrar}
            onClick={() => setMostrarCadastro(true)}
          >
            + Cadastrar Produto
          </button>
        </div>

        {/* Filtros de preço - Mobile */}
        <div className={styles.filtroGrupoMobile}>
          <button
            className={precoFiltro === "todos" ? styles.ativo : ""}
            onClick={() => setPrecoFiltro("todos")}
          >
            Todos
          </button>
          <button
            className={precoFiltro === "baixo" ? styles.ativo : ""}
            onClick={() => setPrecoFiltro("baixo")}
          >
            &lt; R$20
          </button>
          <button
            className={precoFiltro === "medio" ? styles.ativo : ""}
            onClick={() => setPrecoFiltro("medio")}
          >
            R$20–R$50
          </button>
          <button
            className={precoFiltro === "alto" ? styles.ativo : ""}
            onClick={() => setPrecoFiltro("alto")}
          >
            &gt; R$50
          </button>
        </div>
      </div>

      {/* Tabela para Desktop — agrupada por seção */}
      <div className={styles.secoesDesktop}>
        {secoes.length === 0 && (
          <p className={styles.msg}>Nenhum produto encontrado com os filtros aplicados.</p>
        )}
        {secoes.map((secao) => (
          <section key={secao.id} className={styles.secaoCardapio}>
            <h3 className={styles.secaoTitulo}>
              {secao.nome} <span className={styles.secaoContagem}>· {secao.produtos.length} {secao.produtos.length === 1 ? 'item' : 'itens'}</span>
            </h3>
            <div className={styles.cardTabela}>
              <table className={styles.tabela}>
                <thead>
                  <tr>
                    <th>Imagem</th>
                    <th>Nome</th>
                    <th>Descrição</th>
                    <th>Preço</th>
                    <th>DISPONÍVEL</th>
                    <th>Qtd.</th>
                    <th>Lançar</th>
                    <th>Promoção</th>
                    <th>Ação</th>
                  </tr>
                </thead>
                <tbody>
                  {secao.produtos.map((produto) => (
                    <tr key={produto.id_item}>
                      <td>
                        {produto.imagem ? (
                          <img
                            src={produto.imagem}
                            alt={produto.nome_item}
                            className={styles.imagemProduto}
                          />
                        ) : (
                          <span className={`${styles.imagemProduto} ${styles.imagemPlaceholder}`}>
                            <MdRestaurantMenu />
                          </span>
                        )}
                      </td>
                      <td>
                        <span className={styles.produtoNomeTabela}>{produto.nome_item}</span>
                      </td>
                      <td>
                        <span className={styles.descricaoTexto}>{produto.descricao}</span>
                      </td>
                      <td>
                        <PrecoFormatado
                          valor={produto.preco}
                          className={styles.precoValor}
                          centavosClassName={styles.precoCentavos}
                        />
                      </td>
                      <td>
                        <span
                          className={`${styles.disponibilidadeBadge} ${
                            produto.disponivel?.toLowerCase() === "sim"
                              ? styles.disponivelSim
                              : styles.disponivelNao
                          }`}
                        >
                          {produto.disponivel?.toUpperCase() || "-"}
                        </span>
                      </td>
                      <td>
                        <span className={styles.quantidadeValor}>
                          {produto.quantidade === null || produto.quantidade === undefined ? 'Ilimitado' : produto.quantidade}
                        </span>
                      </td>
                      <td>
                        <button
                          className={styles.btnLancarTabela}
                          onClick={() => {
                            setProdutoLancarQuantidade(produto);
                            setNovaQuantidade(produto.quantidade || 0);
                          }}
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            className={styles.btnLancarIcon}
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                            />
                          </svg>
                          Lançar
                        </button>
                      </td>
                      <td>
                        <button
                          className={`${styles.btnLancarTabela} ${produto.promocao === 'sim' ? styles.btnPromocaoAtiva : styles.btnPromocaoTabela}`}
                          onClick={() => {
                            setProdutoLancarPromocao(produto);
                            setNovoDesconto(produto.desconto || 0);
                          }}
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            className={styles.btnLancarIcon}
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A2 2 0 013 12V7a4 4 0 014-4z"
                            />
                          </svg>
                          {produto.promocao === 'sim' ? `${produto.desconto || 0}% OFF` : 'Promoção'}
                        </button>
                      </td>
                      <td>
                        <button
                          className={styles.acaoBtn}
                          onClick={() => setProdutoEditando(produto)}
                        >
                          ⋮
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        ))}
      </div>

      {/* Cards para Mobile - Novo Design, agrupados por seção */}
      {mobileTab === "cardapio" && (
        <div className={styles.cardsContainer}>
          {secoes.length === 0 && (
            <p className={styles.msg}>Nenhum produto encontrado com os filtros aplicados.</p>
          )}
          {secoes.map((secao) => (
            <div key={secao.id} className={styles.secaoCardapioMobile}>
              <h3 className={styles.secaoTitulo}>
                {secao.nome} <span className={styles.secaoContagem}>· {secao.produtos.length} {secao.produtos.length === 1 ? 'item' : 'itens'}</span>
              </h3>
              {secao.produtos.map((produto) => {
            const disponivelSim = produto.disponivel?.toLowerCase() === "sim";
            return (
              <div key={produto.id_item} className={styles.produtoCardNovo}>
                {/* Topo: Imagem + Info + Toggle */}
                <div className={styles.cardTopSection}>
                  {produto.imagem ? (
                    <img
                      src={produto.imagem}
                      alt={produto.nome_item}
                      className={styles.produtoImagem}
                    />
                  ) : (
                    <span className={`${styles.produtoImagem} ${styles.imagemPlaceholder}`}>
                      <MdRestaurantMenu />
                    </span>
                  )}
                  <div className={styles.produtoInfo}>
                    <h3 className={styles.produtoTitulo}>
                      {produto.nome_item}
                      {produto.destaque === "sim" && (
                        <MdStar className={styles.destaqueIcon} title="Produto em destaque" />
                      )}
                    </h3>
                    {produto.descricao && (
                      <p className={`${styles.produtoDescricaoCard} ${!disponivelSim ? styles.textoIndisponivel : ''}`}>
                        {produto.descricao}
                      </p>
                    )}
                    <div className={styles.produtoRodapeCard}>
                      <span className={styles.produtoCategoriaPill}>
                        {mapaCategorias[produto.id_categoria] || "Sem seção"}
                      </span>
                      <PrecoFormatado
                        valor={produto.preco}
                        className={`${styles.produtoPrecoCard} ${!disponivelSim ? styles.textoIndisponivel : ''}`}
                        centavosClassName={styles.precoCentavos}
                      />
                    </div>
                  </div>
                  <div className={styles.toggleAtivo}>
                    <label className={styles.toggleSwitch}>
                      <input
                        type="checkbox"
                        checked={disponivelSim}
                        onChange={() => handleToggleDisponibilidade(produto)}
                      />
                      <span className={styles.toggleSlider}></span>
                    </label>
                  </div>
                </div>

                {/* Divisor */}
                <div className={styles.cardDivider}></div>

                {/* Botões de ação */}
                <div className={styles.cardActions}>
                  <button
                    className={styles.btnLancarCard}
                    onClick={(e) => {
                      e.stopPropagation();
                      setProdutoLancarQuantidade(produto);
                      setNovaQuantidade(produto.quantidade || 0);
                    }}
                  >
                    Lançar
                  </button>

                  <button
                    className={`${styles.btnPromocaoCard} ${produto.promocao === 'sim' ? styles.btnPromocaoCardAtiva : ''}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      setProdutoLancarPromocao(produto);
                      setNovoDesconto(produto.desconto || 0);
                    }}
                  >

                    {produto.promocao === 'sim' ? `${produto.desconto || 0}% OFF` : 'Promoção'}
                  </button>

                  <button
                    className={styles.btnEditarCard}
                    onClick={(e) => {
                      e.stopPropagation();
                      setProdutoEditando(produto);
                    }}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      className={styles.btnEditarIcon}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                      />
                    </svg>
                  </button>
                </div>
              </div>
            );
              })}
            </div>
          ))}
        </div>
      )}

      {/* Aba: Novo Item */}
      {mobileTab === "novoItem" && (
        <div className={styles.novoItemMobile}>
          <button
            type="button"
            className={styles.btnImportarPdfMobile}
            onClick={() => setMostrarImportarPdf(true)}
          >
            <MdUploadFile /> Importar cardápio via PDF
          </button>
          <CadastrarProdutoPopUp onClose={() => setMobileTab("cardapio")} />
        </div>
      )}

      {/* Aba: Promoções */}
      {mobileTab === "promocoes" && (
        <div className={styles.promocoesTab}>
          <div className={styles.promocaoFormCard}>
            <h3 className={styles.promocaoFormTitle}>Lançar Promoção</h3>

            <div className={styles.promocaoInputGroup}>
              <label>Item do cardápio</label>
              <select
                className={styles.promocaoSelect}
                value={mobilePromoItemId}
                onChange={(e) => setMobilePromoItemId(e.target.value)}
              >
                <option value="">Selecione um item...</option>
                {listaBase.map((p) => (
                  <option key={p.id_item} value={p.id_item}>
                    {p.nome_item}
                  </option>
                ))}
              </select>
            </div>

            <div className={styles.promocaoInputGroup}>
              <label>Desconto (%)</label>
              <input
                type="number"
                min="1"
                max="100"
                placeholder="Ex: 20"
                className={styles.promocaoInputDesconto}
                value={mobilePromoDesconto}
                onChange={(e) => setMobilePromoDesconto(e.target.value)}
              />
            </div>

            <button
              className={styles.btnLancarPromocaoMobile}
              onClick={handleLancarPromocaoMobile}
              disabled={!mobilePromoItemId || !mobilePromoDesconto || Number(mobilePromoDesconto) <= 0}
            >
              Lançar Promoção
            </button>
          </div>

          <h4 className={styles.promocoesAtivasTitle}>Promoções Ativas</h4>

          {produtosComPromocao.length === 0 ? (
            <p className={styles.promocoesVazias}>Nenhuma promoção ativa no momento.</p>
          ) : (
            <div className={styles.promocoesAtivasLista}>
              {produtosComPromocao.map((produto) => (
                <div key={produto.id_item} className={styles.promocaoAtivaCard}>
                  {produto.imagem ? (
                    <img
                      src={produto.imagem}
                      alt={produto.nome_item}
                      className={styles.promocaoAtivaImg}
                    />
                  ) : (
                    <span className={`${styles.promocaoAtivaImg} ${styles.imagemPlaceholder}`}>
                      <MdRestaurantMenu />
                    </span>
                  )}
                  <div className={styles.promocaoAtivaInfo}>
                    <span className={styles.promocaoAtivaNome}>{produto.nome_item}</span>
                    <div className={styles.promocaoAtivaPrecos}>
                      <span className={styles.promocaoBadgeDesconto}>-{produto.desconto}%</span>
                      <span className={styles.precoOriginalRiscado}>
                        R$ {Number(produto.preco).toFixed(2)}
                      </span>
                      <span className={styles.precoComDesconto}>
                        R$ {(Number(produto.preco) * (1 - Number(produto.desconto) / 100)).toFixed(2)}
                      </span>
                    </div>
                  </div>
                  <label className={styles.toggleSwitch}>
                    <input
                      type="checkbox"
                      checked={true}
                      onChange={() => handleRemoverPromocaoMobile(produto)}
                    />
                    <span className={styles.toggleSlider}></span>
                  </label>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Pop-ups */}
      {produtoEditando && (
        <EditarProdutoPopUp
          produto={produtoEditando}
          categorias={categorias}
          onClose={() => setProdutoEditando(null)}
          onSalvar={(produtoAtualizado) => {
            const novosProdutos = produtos.map((p) =>
              p.id_item === produtoAtualizado.id_item ? produtoAtualizado : p
            );
            setProdutos(novosProdutos);
            setProdutoEditando(null);
          }}
        />
      )}


      {mostrarCadastro && (
        <div className={styles.modalOverlay} onClick={() => setMostrarCadastro(false)}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <button className={styles.modalClose} onClick={() => setMostrarCadastro(false)}>
              ×
            </button>
            <CadastrarProdutoPopUp onClose={() => setMostrarCadastro(false)} />
          </div>
        </div>
      )}

      {mostrarImportarPdf && (
        <div className={styles.modalOverlay} onClick={fecharModalPdf}>
          <div className={styles.modalContentPdf} onClick={(e) => e.stopPropagation()}>
            <button className={styles.modalClose} onClick={fecharModalPdf}>
              ×
            </button>
            <div className={styles.pdfModalInner}>
              <h2 className={styles.pdfModalTitle}>Importar Cardápio via PDF</h2>
              <p className={styles.pdfModalDescription}>
                Faça upload do cardápio em PDF para extrair e cadastrar todos os produtos automaticamente
              </p>
              <div className={styles.pdfInputGroup}>
                <input
                  ref={fileInputPdfRef}
                  type="file"
                  accept="application/pdf"
                  onChange={handleArquivoPdf}
                  className={styles.pdfFileInput}
                  disabled={carregandoPdf}
                />
                {arquivoPdf && (
                  <span className={styles.pdfFileName}>
                    Arquivo selecionado: {arquivoPdf.name}
                  </span>
                )}
              </div>
              <button
                type="button"
                onClick={processarPdfCardapio}
                className={styles.btnProcessarPdf}
                disabled={!arquivoPdf || carregandoPdf}
              >
                {carregandoPdf ? "Processando PDF..." : "Processar e Cadastrar Produtos"}
              </button>
              {resultadoPdf && <div className={styles.pdfResultado}>{resultadoPdf}</div>}
            </div>
          </div>
        </div>
      )}

      {produtoLancarQuantidade && (
        <div className={styles.modalOverlay} onClick={() => setProdutoLancarQuantidade(null)}>
          <div className={styles.modalQuantidade} onClick={(e) => e.stopPropagation()}>
            <h3>Lançar Quantidade do Dia</h3>
            <p className={styles.produtoNomeModal}>{produtoLancarQuantidade.nome_item}</p>

            <div className={styles.quantidadeAtual}>
              <span>Quantidade Atual:</span>
              <strong>
                {produtoLancarQuantidade.quantidade === null || produtoLancarQuantidade.quantidade === undefined
                  ? 'Ilimitado'
                  : produtoLancarQuantidade.quantidade}
              </strong>
            </div>

            <label className={styles.labelQuantidade}>Quantidade do Dia:</label>
            <input
              type="number"
              min="0"
              value={novaQuantidade}
              onChange={(e) => setNovaQuantidade(Number(e.target.value))}
              className={styles.inputQuantidade}
              placeholder="Informe a quantidade disponível hoje"
            />

            <div className={styles.botoesModal}>
              <button
                className={styles.btnCancelar}
                onClick={() => setProdutoLancarQuantidade(null)}
              >
                Cancelar
              </button>
              <button
                className={styles.btnSalvar}
                onClick={handleLancarQuantidade}
              >
                Salvar
              </button>
            </div>
          </div>
        </div>
      )}

      {produtoLancarPromocao && (
        <div className={styles.modalOverlay} onClick={() => setProdutoLancarPromocao(null)}>
          <div className={styles.modalQuantidade} onClick={(e) => e.stopPropagation()}>
            <h3>Lançar Promoção</h3>
            <p className={styles.produtoNomeModal}>{produtoLancarPromocao.nome_item}</p>

            {produtoLancarPromocao.promocao === 'sim' && (
              <div className={styles.promocaoAtiva}>
                <span>Promoção ativa com {produtoLancarPromocao.desconto || 0}% de desconto</span>
              </div>
            )}

            <label className={styles.labelQuantidade}>Desconto (%):</label>
            <input
              type="number"
              min="0"
              max="100"
              value={novoDesconto}
              onChange={(e) => setNovoDesconto(Number(e.target.value))}
              className={styles.inputQuantidade}
              placeholder="Informe o percentual de desconto"
            />

            {novoDesconto > 0 && novoDesconto <= 100 && (
              <div className={styles.previewPreco}>
                <div className={styles.previewLinha}>
                  <span className={styles.previewLabel}>Preço original:</span>
                  <span className={styles.previewOriginal}>R$ {Number(produtoLancarPromocao.preco).toFixed(2)}</span>
                </div>
                <div className={styles.previewLinha}>
                  <span className={styles.previewLabel}>Novo preço:</span>
                  <span className={styles.previewNovo}>
                    R$ {(Number(produtoLancarPromocao.preco) * (1 - novoDesconto / 100)).toFixed(2)}
                  </span>
                </div>
              </div>
            )}

            <div className={styles.botoesModal}>
              <button
                className={styles.btnCancelar}
                onClick={() => setProdutoLancarPromocao(null)}
              >
                Cancelar
              </button>
              {produtoLancarPromocao.promocao === 'sim' ? (
                <button
                  className={styles.btnRemoverPromocao}
                  onClick={() => handleLancarPromocao(false)}
                >
                  Remover Promoção
                </button>
              ) : null}
              <button
                className={styles.btnSalvar}
                onClick={() => handleLancarPromocao(true)}
                disabled={novoDesconto <= 0 || novoDesconto > 100}
              >
                Lançar Promoção
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ProdutoTabela;
